import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Pause, Play } from 'lucide-react';
import { CrisisFooter } from '@/components/CrisisFooter';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEATHER_OPTIONS = [
  { id: 'stormy', icon: '🌩', label: 'Stormy' },
  { id: 'heavy', icon: '🌧', label: 'Heavy' },
  { id: 'cloudy', icon: '⛅', label: 'In between' },
  { id: 'lifting', icon: '🌤', label: 'Lifting' },
  { id: 'grounded', icon: '☀️', label: 'Grounded' },
];

const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex justify-center gap-2 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div
        key={i}
        className="h-1 rounded-full transition-all duration-500"
        style={{
          width: i + 1 === currentStep ? '32px' : '8px',
          backgroundColor: i + 1 <= currentStep ? '#C9A84C' : '#D5CFC6',
        }}
      />
    ))}
  </div>
);

// Step 1: Arrive
const ArriveStep = ({ onNext }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (hasStarted && !isPaused && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, isPaused, hasStarted]);

  const handleStart = () => {
    setHasStarted(true);
    setIsPaused(false);
  };

  const handleTogglePause = () => {
    if (!hasStarted) {
      handleStart();
    } else {
      setIsPaused(!isPaused);
    }
  };

  return (
    <div className="text-center animate-fadeIn">
      <h2 className="text-xs tracking-[0.2em] uppercase mb-6" style={{ color: '#C9A84C' }}>
        STEP 1 — ARRIVE
      </h2>
      
      <p className="text-xl md:text-2xl leading-relaxed mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
        Take one breath before anything else.
      </p>
      <p className="text-lg mb-12" style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}>
        You are here. That is enough.
      </p>

      {/* Breathing Circle */}
      <div className="flex justify-center mb-8">
        <div
          className="w-32 h-32 rounded-full flex items-center justify-center transition-all duration-1000"
          style={{
            background: hasStarted && !isPaused
              ? 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(201,168,76,0.1) 100%)'
              : 'radial-gradient(circle, rgba(201,168,76,0.2) 0%, rgba(201,168,76,0.05) 100%)',
            boxShadow: hasStarted && !isPaused ? '0 0 40px rgba(201,168,76,0.3)' : 'none',
            transform: hasStarted && !isPaused ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          <span className="text-2xl font-light" style={{ color: '#C9A84C' }}>
            {hasStarted ? countdown : '30'}
          </span>
        </div>
      </div>

      <Button
        onClick={handleTogglePause}
        variant="outline"
        className="rounded-full px-8 py-6 mb-8"
        style={{ borderColor: '#C9A84C', color: '#C9A84C' }}
        data-testid="pause-button"
      >
        {(() => {
          if (!hasStarted) {
            return (
              <>
                <Play className="w-4 h-4 mr-2" />
                Begin
              </>
            );
          }
          if (isPaused) {
            return (
              <>
                <Play className="w-4 h-4 mr-2" />
                Resume
              </>
            );
          }
          return (
            <>
              <Pause className="w-4 h-4 mr-2" />
              Pause
            </>
          );
        })()}
      </Button>

      {(countdown === 0 || hasStarted) && (
        <div>
          <Button
            onClick={onNext}
            className="rounded-full px-8 py-6"
            style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
            data-testid="next-step-button"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

// Step 2: Check In
const CheckInStep = ({ onNext, onSelectMood, token }) => {
  const [selectedWeather, setSelectedWeather] = useState(null);

  const handleSelect = async (weather) => {
    setSelectedWeather(weather.id);
    onSelectMood(weather.id);
    
    // Save mood
    try {
      await axios.post(
        `${API}/mood/save`,
        { mood: weather.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Mood save failure is non-critical - continue ritual
              logError('Mood save failed:', error?.response?.status);
    }
  };

  return (
    <div className="text-center animate-fadeIn">
      <h2 className="text-xs tracking-[0.2em] uppercase mb-6" style={{ color: '#C9A84C' }}>
        STEP 2 — CHECK IN
      </h2>
      
      <p className="text-xl md:text-2xl leading-relaxed mb-12" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
        How are you arriving this morning?
      </p>

      <div className="flex justify-center gap-4 mb-12 flex-wrap">
        {WEATHER_OPTIONS.map((weather) => (
          <button
            key={weather.id}
            onClick={() => handleSelect(weather)}
            className="flex flex-col items-center transition-all duration-300"
            data-testid={`weather-${weather.id}`}
          >
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-2 transition-all duration-300 ${
                selectedWeather === weather.id ? 'scale-110' : 'hover:scale-105'
              }`}
              style={{
                background: selectedWeather === weather.id
                  ? 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(201,168,76,0.1) 100%)'
                  : 'radial-gradient(circle, rgba(232,180,184,0.15) 0%, rgba(232,180,184,0.05) 100%)',
                boxShadow: selectedWeather === weather.id ? '0 0 20px rgba(201,168,76,0.4)' : 'none',
              }}
            >
              {weather.icon}
            </div>
            <span
              className="text-xs transition-colors duration-300"
              style={{ color: selectedWeather === weather.id ? '#C9A84C' : '#8A8798' }}
            >
              {weather.label}
            </span>
          </button>
        ))}
      </div>

      {selectedWeather && (
        <Button
          onClick={onNext}
          className="rounded-full px-8 py-6 animate-fadeIn"
          style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
          data-testid="next-step-button"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}
    </div>
  );
};

// Step 3: Set Intention
const IntentionStep = ({ onNext, token }) => {
  const [intention, setIntention] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!intention.trim()) {
      toast.error('Please set your intention');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/journal/save`,
        {
          prompt_id: 'morning-intention',
          entry_text: intention
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onNext();
    } catch {
      toast.error('Failed to save intention');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-center animate-fadeIn">
      <h2 className="text-xs tracking-[0.2em] uppercase mb-6" style={{ color: '#C9A84C' }}>
        STEP 3 — SET INTENTION
      </h2>
      
      <p className="text-xl md:text-2xl leading-relaxed mb-8" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
        What do you want to feel by end of today?
      </p>

      <div className="max-w-md mx-auto mb-8">
        <Textarea
          value={intention}
          onChange={(e) => setIntention(e.target.value)}
          placeholder="I want to feel..."
          className="min-h-[120px] rounded-xl border-0 text-center text-lg"
          style={{
            backgroundColor: '#F7F1EE',
            color: '#1A1A2E',
            fontFamily: 'Playfair Display, serif',
          }}
          data-testid="intention-input"
        />
      </div>

      <Button
        onClick={handleSave}
        disabled={!intention.trim() || saving}
        className="rounded-full px-8 py-6"
        style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
        data-testid="save-intention-button"
      >
        {saving ? 'Saving...' : 'Set Intention'}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

// Step 4: Today's Reflection
const ReflectionStep = ({ onNext, dailyPrompt, navigate }) => {
  const handleExploreWithSage = () => {
    // Navigate to chat with the prompt pre-loaded
    navigate('/chat', { state: { preloadedMessage: dailyPrompt } });
  };

  return (
    <div className="text-center animate-fadeIn">
      <h2 className="text-xs tracking-[0.2em] uppercase mb-6" style={{ color: '#C9A84C' }}>
        STEP 4 — TODAY'S REFLECTION
      </h2>

      {/* Gold Shimmer Card */}
      <div
        className="max-w-md mx-auto mb-8 p-8 rounded-2xl relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(201,168,76,0.15) 0%, rgba(201,168,76,0.05) 100%)',
          border: '1px solid rgba(201,168,76,0.3)',
          boxShadow: '0 0 40px rgba(201,168,76,0.1)',
        }}
      >
        {/* Shimmer effect */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.2), transparent)',
            animation: 'shimmer 3s infinite',
          }}
        />
        <p
          className="text-lg md:text-xl leading-relaxed relative z-10"
          style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
        >
          "{dailyPrompt}"
        </p>
      </div>

      <Button
        onClick={handleExploreWithSage}
        className="rounded-full px-8 py-6 mb-4"
        style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
        data-testid="explore-with-sage-button"
      >
        Explore this with Sage
      </Button>

      <div>
        <button
          onClick={onNext}
          className="text-sm underline hover:no-underline transition-colors"
          style={{ color: '#6B6878' }}
          data-testid="skip-button"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

// Step 5: Close
const CloseStep = ({ navigate }) => {
  return (
    <div className="text-center animate-fadeIn">
      <h2 className="text-xs tracking-[0.2em] uppercase mb-6" style={{ color: '#C9A84C' }}>
        STEP 5 — CLOSE
      </h2>

      <div className="mb-12">
        <p className="text-xl md:text-2xl leading-relaxed mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
          Your sanctuary is open.
        </p>
        <p className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}>
          Sage is here whenever you need her.
        </p>
      </div>

      {/* Soft glow orb */}
      <div className="flex justify-center mb-12">
        <div
          className="w-24 h-24 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(201,168,76,0.6) 0%, rgba(232,180,184,0.3) 50%, transparent 100%)',
            boxShadow: '0 0 60px rgba(201,168,76,0.4)',
            animation: 'pulse 3s infinite',
          }}
        />
      </div>

      <Button
        onClick={() => navigate('/sanctuary')}
        className="rounded-full px-10 py-6 text-lg"
        style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
        data-testid="enter-day-button"
      >
        Enter My Day
      </Button>
    </div>
  );
};

export const MorningRitualPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [fadeIn, setFadeIn] = useState(true);
  const [selectedMood, setSelectedMood] = useState(null);
  const [dailyPrompt, setDailyPrompt] = useState("What is your heart asking you to notice today?");

  // Fetch daily prompt based on user's phase
  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        const response = await axios.get(`${API}/daily-prompt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.prompt_text) {
          setDailyPrompt(response.data.prompt_text);
        }
      } catch (error) {
        // Daily prompt fetch failure - use default prompt
                  logError('Daily prompt fetch failed:', error?.response?.status);
      }
    };
    
    if (token) fetchPrompt();
  }, [token]);

  const goToStep = useCallback((step) => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentStep(step);
      setFadeIn(true);
    }, 300);
  }, []);

  const handleNext = useCallback(() => {
    goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    } else {
      navigate('/sanctuary');
    }
  }, [currentStep, goToStep, navigate]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ArriveStep onNext={handleNext} />;
      case 2:
        return <CheckInStep onNext={handleNext} onSelectMood={setSelectedMood} token={token} />;
      case 3:
        return <IntentionStep onNext={handleNext} token={token} />;
      case 4:
        return <ReflectionStep onNext={handleNext} dailyPrompt={dailyPrompt} navigate={navigate} />;
      case 5:
        return <CloseStep navigate={navigate} />;
      default:
        return <ArriveStep onNext={handleNext} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FBF9F6' }}>
      {/* Header */}
      <header className="p-6">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <button
            onClick={handleBack}
            className="p-2 rounded-full transition-colors hover:bg-[#F7F1EE]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#6B6878' }} />
          </button>
          <div className="text-center">
            <h1
              className="text-2xl font-medium"
              style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}
            >
              Your Morning Ritual
            </h1>
            <p className="text-sm" style={{ color: '#6B6878' }}>
              Begin with intention
            </p>
          </div>
          <div className="w-9" /> {/* Spacer for alignment */}
        </div>
      </header>

      {/* Progress Indicator */}
      <StepIndicator currentStep={currentStep} totalSteps={5} />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 pb-24">
        <div
          className={`w-full max-w-md transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          {renderStep()}
        </div>
      </main>

      {/* Crisis Footer */}
      <CrisisFooter />

      {/* CSS for animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MorningRitualPage;
